/**
 * parseArtifactsFromToolEvent — extend the parser surface from
 * inventory-only items to the broader artifact set (#260).
 *
 * Each adapter targets ONE BE tool whose shape we've captured live.
 * The renderers in `components/artifacts/` mount whichever artifacts
 * an AssistantBubble produced.
 *
 * Tools we render artifacts for (v1):
 *   - `who_has_item`     → CustodyArtifact   (compact chip)
 *   - `find_by_location` → LocationGroupArtifact (location header + items)
 *
 * Tools handled elsewhere:
 *   - `search_inventory` / `lookup_inventory_*` → FounsiItemRow
 *     (see `parseItemsFromToolResult`).
 *
 * Tools we don't render (silently no-op):
 *   - `find_item_location` (frequently fails — `founsi_tool_dispatch_failed`)
 *   - `list_items_by_owner` (often returns `visible: false`, nothing
 *      to render).
 *   - Anything we haven't seen yet — forward-compat is opt-in.
 */

export interface CustodyArtifact {
  type: 'custody';
  /** Inventory item id — for the "Open item" tap target. */
  item_id: number;
  item_name: string;
  custodian_name: string;
  /** True when the owner is also the custodian (i.e. item is with
   *  them, not lent out). */
  is_self_custody: boolean;
  /** ISO timestamp when custody started. */
  since: string | null;
}

export interface LocationGroupArtifact {
  type: 'location_group';
  /** The location substring the LLM queried for (e.g. "garage"). */
  location: string;
  /** Items at this location.  `primary_photo_url` is usually null
   *  on the BE side; renderer falls back to the inventory icon. */
  items: Array<{
    item_id: number;
    item_name: string;
    location: string;
    category: string;
    primary_photo_url: string | null;
  }>;
}

export type FounsiArtifact = CustodyArtifact | LocationGroupArtifact;

// ─── Per-tool adapters ─────────────────────────────────────────────

function parseWhoHasItem(data: unknown): FounsiArtifact[] {
  const d = data as any;
  if (!d || d.found !== true) return [];
  if (typeof d.item_id !== 'number' || typeof d.item_name !== 'string') return [];
  if (typeof d.custodian_name !== 'string') return [];
  return [
    {
      type: 'custody',
      item_id: d.item_id,
      item_name: d.item_name,
      custodian_name: d.custodian_name,
      is_self_custody: !!d.is_self_custody,
      since: typeof d.custody_since === 'string' ? d.custody_since : null,
    },
  ];
}

function parseFindByLocation(data: unknown): FounsiArtifact[] {
  const d = data as any;
  const items = d?.items;
  if (!Array.isArray(items) || items.length === 0) return [];
  const location =
    typeof d.location_substring === 'string' ? d.location_substring : 'Location';
  return [
    {
      type: 'location_group',
      location,
      items: items
        .filter(
          (it: any) =>
            it && typeof it.item_id === 'number' && typeof it.item_name === 'string',
        )
        .map((it: any) => ({
          item_id: it.item_id,
          item_name: it.item_name,
          location: typeof it.location === 'string' ? it.location : '',
          category: typeof it.category === 'string' ? it.category : '',
          primary_photo_url:
            typeof it.primary_photo_url === 'string'
              ? it.primary_photo_url
              : null,
        })),
    },
  ];
}

const ADAPTERS: Record<string, (data: unknown) => FounsiArtifact[]> = {
  who_has_item: parseWhoHasItem,
  find_by_location: parseFindByLocation,
};

/** Public entry — returns [] for tools we don't have an adapter for
 *  or for failed/errored tool results. */
export function parseArtifactsFromToolEvent(
  toolName: string,
  status: string,
  data: unknown,
): FounsiArtifact[] {
  if (status !== 'ok') return [];
  if (data == null) return [];
  const adapter = ADAPTERS[toolName];
  if (!adapter) return [];
  return adapter(data);
}
